import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, fmtTime } from "../../lib/api";
import { connectYoutubePopup } from "../../lib/youtubeOauth";
import { Card } from "../ui-bits";
import { toast } from "sonner";
import { Youtube, Eye, ThumbsUp, MessageSquare, Loader2, ExternalLink, Users, Film, Link2 } from "lucide-react";

const compact = (n) => (n == null ? "—" : Intl.NumberFormat("es", { notation: "compact", maximumFractionDigits: 1 }).format(n));

export default function YoutubePublished({ channel: ch0 }) {
  const { t } = useTranslation();
  const channelId = ch0.id;
  const since = (iso) => {
    const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
    if (d < 1) return t("ytpub.today");
    if (d < 30) return t("ytpub.daysAgo", { n: d });
    if (d < 365) return t("ytpub.monthsAgo", { n: Math.floor(d / 30) });
    return t("ytpub.yearsAgo", { n: Math.floor(d / 365) });
  };
  const [videos, setVideos] = useState(null);
  const [channel, setChannel] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [oauthConnecting, setOauthConnecting] = useState(false);

  const loadVideos = () => {
    setBusy(true); setErr("");
    return api.get(`/channels/${channelId}/youtube/videos`)
      .then((r) => { setVideos(r.data.connected ? (r.data.videos || []) : null); setChannel(r.data.channel || null); })
      .catch((e) => setErr(apiErr(e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    let on = true;
    setBusy(true); setErr("");
    api.get(`/channels/${channelId}/youtube/videos`)
      .then((r) => { if (on) { setVideos(r.data.connected ? (r.data.videos || []) : null); setChannel(r.data.channel || null); } })
      .catch((e) => { if (on) setErr(apiErr(e)); })
      .finally(() => { if (on) setBusy(false); });
    return () => { on = false; };
  }, [channelId]);

  const connectOauth = async () => {
    setOauthConnecting(true);
    try {
      await connectYoutubePopup(channelId);
      toast.success(t("ytpub.connected"));
      await loadVideos();
    } catch (e) {
      if (e.message !== "popup_closed") toast.error(apiErr(e));
    } finally { setOauthConnecting(false); }
  };

  const isConnected = videos !== null;

  return (
    <div className="space-y-4" data-testid="youtube-published-section">
      {busy ? (
        <Card className="p-8 flex items-center justify-center gap-3 text-slate-500"><Loader2 className="animate-spin" size={22} /> {t("ytpub.loading")}</Card>
      ) : !isConnected ? (
        <Card className="p-8" data-testid="youtube-connect-card">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 text-slate-900">
              <Link2 size={22} className="text-red-600" />
              <h3 className="text-xl font-bold">{t("ytpub.connectTitle")}</h3>
            </div>
            <p className="text-base text-slate-600 mt-2 mb-4">{t("ytpub.connectDesc")}</p>
            <button data-testid="youtube-connect-btn" onClick={connectOauth} disabled={oauthConnecting}
              className="min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap">
              {oauthConnecting ? <Loader2 className="animate-spin" size={20} /> : <Youtube size={20} />} {t("ytpub.connectBtn")}
            </button>
            {err && <p className="text-sm text-red-600 mt-2">{err}</p>}
          </div>
        </Card>
      ) : (<>

      {channel && (
        <div data-testid="youtube-channel-header" className="flex items-center gap-4 border-2 border-slate-200 rounded-xl p-4 bg-white">
          {channel.thumbnail && <img src={channel.thumbnail} alt={channel.title} className="w-16 h-16 rounded-full object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <a href={channel.url} target="_blank" rel="noreferrer" className="text-lg font-bold text-slate-900 hover:text-blue-700 truncate block">{channel.title}</a>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-base text-slate-600 mt-1">
              {channel.subscribers != null && <span data-testid="yt-header-subs" className="flex items-center gap-1.5"><Users size={16} /> {channel.subscribers.toLocaleString()} {t("ytpub.subs")}</span>}
              <span data-testid="yt-header-views" className="flex items-center gap-1.5"><Eye size={16} /> {channel.views.toLocaleString()} {t("ytpub.views")}</span>
              <span data-testid="yt-header-videos" className="flex items-center gap-1.5"><Film size={16} /> {channel.videos.toLocaleString()} {t("ytpub.videos")}</span>
            </div>
          </div>
        </div>
      )}

      {err ? (
        <Card className="p-6 text-center text-slate-600">{err}</Card>
      ) : videos.length === 0 ? (
        <Card className="p-6 text-center text-slate-600">{t("ytpub.noVideos")}</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-testid="youtube-videos-grid">
          {videos.map((v) => (
            <a key={v.id} href={v.url} target="_blank" rel="noreferrer" data-testid={`yt-video-${v.id}`}
               className="group block rounded-xl overflow-hidden border-2 border-slate-200 bg-white hover:border-blue-300 hover:shadow-md transition-all">
              <div className="relative aspect-video bg-slate-100">
                {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />}
                <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-semibold px-1.5 py-0.5 rounded">{fmtTime(v.duration)}</span>
                <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-slate-700 rounded-full p-1.5"><ExternalLink size={15} /></span>
              </div>
              <div className="p-3">
                <p className="text-base font-semibold text-slate-900 line-clamp-2 leading-snug">{v.title}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 mt-2">
                  <span className="flex items-center gap-1"><Eye size={14} /> {compact(v.views)}</span>
                  <span className="flex items-center gap-1"><ThumbsUp size={14} /> {compact(v.likes)}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={14} /> {compact(v.comments)}</span>
                  <span>· {since(v.published_at)}</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
